import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Firestore, collection, collectionData, doc, setDoc, deleteDoc } from '@angular/fire/firestore';
import { Observable, firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';

import { resolveSmartAgentIcon, resolveSmartAgentBadge } from '../agent-chat/agent-chat.component';

export interface AgentRecord {
  agentId: string;
  name: string;
  description: string;
  metaprompt: string;
  isActive: boolean;
  icon?: string;
  badge?: string;
  starterPrompts?: string[];
  cachedContentName?: string;
  updatedAt?: any;
}

@Component({
  selector: 'app-agent-management',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './agent-management.component.html',
  styleUrls: ['./agent-management.component.css']
})
export class AgentManagementComponent implements OnInit {
  private firestore = inject(Firestore);
  private http = inject(HttpClient);

  agents$: Observable<AgentRecord[]> | null = null;
  blueprintCache: any = null;

  isModalOpen = false;
  isSaving = false;
  editingId: string | null = null;
  currentAgent: AgentRecord = this.getDefaultAgent();

  resolveIcon(agent: AgentRecord): string {
    return resolveSmartAgentIcon(agent.agentId, agent.name, agent.icon);
  }

  resolveBadge(agent: AgentRecord): string {
    return resolveSmartAgentBadge(agent.agentId, agent.name, agent.badge);
  }

  ngOnInit() {
    this.loadBlueprintCache();
    const agentsCol = collection(this.firestore, 'agents');
    // Using idField: 'agentId' ensures document ID matches the agentId property
    this.agents$ = collectionData(agentsCol, { idField: 'agentId' }).pipe(
      map(agents => (agents as AgentRecord[]).sort((a, b) => (a.name || '').localeCompare(b.name || '')))
    );
  }

  async loadBlueprintCache() {
    try {
      this.blueprintCache = await firstValueFrom(this.http.get<any>('assets/blueprint-cache.json'));
    } catch (e) {
      // assets/blueprint-cache.json not available
    }
  }

  private getDefaultAgent(): AgentRecord {
    return {
      agentId: '',
      name: '',
      description: '',
      metaprompt: '',
      isActive: true
    };
  }

  openAddModal() {
    this.editingId = null;
    this.currentAgent = this.getDefaultAgent();
    this.isModalOpen = true;
  }

  editAgent(agent: AgentRecord) {
    this.editingId = agent.agentId;
    this.currentAgent = { ...agent };
    this.isModalOpen = true;
  }

  closeModal() {
    this.isModalOpen = false;
    this.currentAgent = this.getDefaultAgent();
    this.editingId = null;
  }

  async saveAgent() {
    const targetAgentId = (this.editingId || this.currentAgent.agentId || '').trim();
    if (!targetAgentId) {
      alert('Agent ID is required.');
      return;
    }
    if (!this.currentAgent.name || !this.currentAgent.metaprompt) {
      alert('Agent Name and Metaprompt are required fields.');
      return;
    }

    this.isSaving = true;
    try {
      // Key invariant: The document ID in the 'agents' collection MUST be strictly the agentId (not an auto-id UID)
      const docRef = doc(this.firestore, 'agents', targetAgentId);
      const payload: any = {
        agentId: targetAgentId,
        name: this.currentAgent.name,
        description: this.currentAgent.description || '',
        metaprompt: this.currentAgent.metaprompt,
        isActive: this.currentAgent.isActive !== false,
        updatedAt: new Date()
      };

      if (this.currentAgent.icon) payload.icon = this.currentAgent.icon;
      if (this.currentAgent.badge) payload.badge = this.currentAgent.badge;
      if (this.currentAgent.starterPrompts) payload.starterPrompts = this.currentAgent.starterPrompts;
      if (this.currentAgent.cachedContentName) payload.cachedContentName = this.currentAgent.cachedContentName;

      await setDoc(docRef, payload, { merge: true });

      this.closeModal();
    } catch (error) {
      console.error('Error saving agent:', error);
      alert('Failed to save agent: ' + (error as any)?.message);
    } finally {
      this.isSaving = false;
    }
  }

  async deleteAgent(agentId: string) {
    if (confirm(`Are you sure you want to delete agent '${agentId}'?`)) {
      try {
        const docRef = doc(this.firestore, 'agents', agentId);
        await deleteDoc(docRef);
      } catch (error) {
        console.error('Error deleting agent:', error);
        alert('Failed to delete agent.');
      }
    }
  }
}
